/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2018, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS 'AS IS' AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React, { Fragment } from 'react'
import PropTypes from 'prop-types'

import {
  IdentityAddressAdd,
  AddressEdit,
  SetUserEmail,
  SvgIcon
} from '../../'

import {
  AddressPropertyIndicator
} from './AddressPropertyIndicator'

import {
  readablePhoneNumber
} from '../../../utils'

const getAddressTypeLabel = (addressType) => (
  {
    'msisdn': 'Cellphone',
    'landline': 'Landline',
    'email': 'Email',
    'physical': 'Physical'
  }[addressType]
)


const AddressDisplayEdit = ({ addresses, addressType, identity, identityFirstName, add, onAddCancel, onAddSuccess, onAddFail, edit, saveEdited, onEditSuccess, onEditFail, showLoginButton }) => {
  let type = getAddressTypeLabel(addressType)

  if (addresses.length === 0) {
    return (
      <tbody>
        <tr>
          <td colSpan='2'>
            {`No active ${type.toLowerCase()} addresses.`}
          </td>
        </tr>
        {add &&
          <tr>
            <th className='slds-text-heading_label slds-theme_shade clientDetails-address-type'>
              {type}
            </th>
            <td className='slds-theme_shade'>
              <IdentityAddressAdd
                addressType={addressType}
                identity={identity}
                onCancelClick={onAddCancel}
                onAdded={onAddSuccess}
                onFailed={onAddFail}
              />
            </td>
          </tr>
        }
      </tbody>
    )
  } else {
    return (
      <tbody>
        {addresses.map(({ node: address }) => (
          <tr key={`${type}_${address.id}`}>
            {edit
              ? <Fragment>
                  <th className='slds-size_1-of-4 slds-theme_shade slds-text-heading_label clientDetails-address-type'>
                    {type}
                  </th>
                  <td className='slds-theme_shade'>
                    <AddressEdit
                      firstName={identityFirstName}
                      addressId={address.id}
                      addressType={address.addressType}
                      addressValue={address.addressValue}
                      default={address.default}
                      optedOut={address.optedOut}
                      save={saveEdited}
                      onSaved={onEditSuccess}
                      onFailed={onEditFail}
                    />
                  </td>
                </Fragment>
              : <Fragment>
                  <th className='slds-size_1-of-4 slds-text-heading_label'>
                    {type}
                  </th>
                  <td className={`${addressType === 'physical' ? 'slds-wrap' : ''}`}>
                    <div className='slds-grid slds-m-left_small slds-m-right_medium'>
                      <div>
                        {addressType === 'physical'
                          ? address.addressValue.replace(/\n/g, ', ') // replace line breaks with commas
                          : [ 'msisdn', 'landline' ].indexOf(addressType) > -1
                              ? readablePhoneNumber(address.addressValue)
                              : address.addressValue
                        }
                      </div>
                      {showLoginButton
                        ? <Fragment>
                            <div className='slds-col_bump-left'>
                              <SetUserEmail
                                identity={identity}
                                email={address.addressValue}
                                renderSet={
                                  <div className='slds-text-align_right'>
                                    <SvgIcon
                                      variant='utility'
                                      symbol='check'
                                      size='x-small'
                                      className='slds-m-left_x-small slds-m-right_xx-small'
                                    />
                                    Set for login
                                  </div>
                                }
                              />
                            </div>
                            <div className='slds-m-left_medium'>
                              {address.verified &&
                                <AddressPropertyIndicator colour='green'>
                                  Verified
                                </AddressPropertyIndicator>
                              }
                            </div>
                            <div className='slds-m-left_medium'>
                              {!address.optedOut &&
                                <AddressPropertyIndicator>
                                  Opted in
                                </AddressPropertyIndicator>
                              }
                            </div>
                          </Fragment>
                        : <Fragment>
                            <div className='slds-col_bump-left'>
                              {!address.verifed &&
                                <AddressPropertyIndicator colour='green'>
                                  Verified
                                </AddressPropertyIndicator>
                              }
                            </div>
                            <div className='slds-m-left_medium'>
                              {!address.optedOut &&
                                <AddressPropertyIndicator>
                                  Opted in
                                </AddressPropertyIndicator>
                              }
                            </div>
                          </Fragment>
                      }
                      <div className='slds-m-left_medium slds-text-align_right slds-size_1-of-12'>
                        {address.default && <b>Primary</b>}
                      </div>
                    </div>
                  </td>
                </Fragment>
            }
          </tr>
        ))}
        {add &&
          <tr>
            <th
              className='slds-size_1-of-4 slds-theme_shade slds-text-heading_label clientDetails-address-type'
              style={{ boxShadow: '2px 1px 8px 2px rgb(150 68 251 / 25%)', clipPath: 'inset(-5px 0px -5px -5px)' }}>
              {type}
            </th>
            <td
              className='slds-theme_shade'
              style={{ boxShadow: '2px 1px 8px 2px rgb(150 68 251 / 25%)', clipPath: 'inset(-5px -5px -5px 0px)' }}>
              <IdentityAddressAdd
                addressType={addressType}
                identity={identity}
                onCancelClick={onAddCancel}
                onAdded={onAddSuccess}
                onFailed={onAddFail}
              />
            </td>
          </tr>
        }
      </tbody>
    )
  }
}

AddressDisplayEdit.propTypes = {
  addresses: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    addressType: PropTypes.oneOf([ 'msisdn', 'landline', 'email', 'physical' ]),
    addressValue: PropTypes.string,
    default: PropTypes.bool,
    optedOut: PropTypes.bool
  })).isRequired,
  addressType: PropTypes.oneOf([ 'msisdn', 'landline', 'email', 'physical' ]),
  identity: PropTypes.string.isRequired,
  identityFirstName: PropTypes.string.isRequired,
  add: PropTypes.bool.isRequired,
  onAddCancel: PropTypes.func.isRequired,
  onAddSuccess: PropTypes.func.isRequired,
  onAddFail: PropTypes.func.isRequired,
  edit: PropTypes.bool.isRequired,
  saveEdited: PropTypes.bool.isRequired,
  onEditSuccess: PropTypes.func.isRequired,
  onEditFail: PropTypes.func.isRequired,
  showLoginButton: PropTypes.bool
}

AddressDisplayEdit.defaultProps = {
  showLoginButton: false
}

export default AddressDisplayEdit



// WEBPACK FOOTER //
// ./src/components/messageplus/employee-contact-details/AddressDisplayEdit.js